import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
} from 'components/mui';
import {
  Paper,
  Skeleton,
  CircularProgress,
  TextField,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import RichTextEditor from 'components/RichTextEditor';
import DwpCaseSetup from './DwpCaseSetup';
import type { CANtrackComment } from 'containers/pages/comments/types/comment';
import type { MyCaseSummary, PinnedCase } from 'containers/pages/cantrackDashboard/types';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface DailyWorkPlanCardProps {
  hasDailyPlanCase: boolean;
  planSubmittedToday: boolean;
  todaysPlan: CANtrackComment | null;
  loading: boolean;
  myCases: MyCaseSummary[];
  pinnedCases: PinnedCase[];
  onSubmit: (summary: string, description: string) => Promise<void>;
  onUpdate: (summary: string, description: string) => Promise<void>;
  onConfigureCaseId: (caseId: number) => void;
}

/* ------------------------------------------------------------------ */
/*  DailyWorkPlanCard                                                  */
/* ------------------------------------------------------------------ */

function DailyWorkPlanCard({
  hasDailyPlanCase,
  planSubmittedToday,
  todaysPlan,
  loading,
  myCases,
  pinnedCases,
  onSubmit,
  onUpdate,
  onConfigureCaseId,
}: DailyWorkPlanCardProps) {
  const [summary, setSummary] = useState('');
  const [description, setDescription] = useState('');
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  const handleSubmit = async () => {
    if (!summary.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit(summary, description);
      setSummary('');
      setDescription('');
    } catch {
      // Error already handled by hook (logged + user notified)
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!summary.trim()) return;
    setSubmitting(true);
    try {
      await onUpdate(summary, description);
      setEditing(false);
      setSummary('');
      setDescription('');
    } catch {
      // Error already handled by hook (logged + user notified)
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartEdit = () => {
    setSummary(todaysPlan?.title || '');
    setDescription(todaysPlan?.description || '');
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setSummary('');
    setDescription('');
  };

  if (!hasDailyPlanCase || showConfig) {
    return (
      <DwpCaseSetup
        myCases={myCases}
        pinnedCases={pinnedCases}
        isReconfigure={hasDailyPlanCase}
        onSelect={(caseId) => {
          onConfigureCaseId(caseId);
          setShowConfig(false);
        }}
        onCancel={hasDailyPlanCase ? () => setShowConfig(false) : undefined}
      />
    );
  }

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Skeleton variant="text" width={200} height={28} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
      </Paper>
    );
  }

  // Plan already submitted — show it
  if (planSubmittedToday && todaysPlan && !editing) {
    return (
      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleOutlineIcon fontSize="small" color="success" />
            <Typography variant="subtitle1" fontWeight={600}>
              Daily Work Plan
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Tooltip title="Edit plan">
              <IconButton size="small" onClick={handleStartEdit}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Configure DWP case">
              <IconButton size="small" onClick={() => setShowConfig(true)}>
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        <Box sx={{ pl: 3.5 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            {todaysPlan.title}
          </Typography>
          {todaysPlan.description && (
            <Box
              sx={{ typography: 'body2', color: 'text.secondary', '& p': { m: 0 } }}
              dangerouslySetInnerHTML={{ __html: todaysPlan.description }}
            />
          )}
        </Box>
      </Paper>
    );
  }

  // No plan submitted or editing — show editor
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderLeft: !planSubmittedToday ? 4 : undefined,
        borderLeftColor: !planSubmittedToday ? 'primary.main' : undefined,
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
        {editing ? 'Edit Daily Work Plan' : 'What are you working on today?'}
      </Typography>

      <TextField
        label="Summary"
        placeholder="Brief summary of today's plan..."
        size="small"
        fullWidth
        value={summary}
        onChange={(e) => setSummary(e.target.value)}
        sx={{ mb: 1.5 }}
      />

      <RichTextEditor
        value={description}
        onChange={setDescription}
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          mt: 1.5,
        }}
      >
        {editing && (
          <Tooltip title={submitting ? 'Please wait while saving' : ''}>
            <span>
              <Button
                size="small"
                onClick={handleCancelEdit}
                disabled={submitting}
              >
                Cancel
              </Button>
            </span>
          </Tooltip>
        )}
        <Tooltip title={!summary.trim() && !submitting ? 'Enter a summary before submitting' : ''}>
          <span>
            <Button
              variant="contained"
              size="small"
              onClick={editing ? handleUpdate : handleSubmit}
              disabled={submitting || !summary.trim()}
              startIcon={
                submitting ? <CircularProgress size={16} color="inherit" /> : undefined
              }
            >
              {submitting
                ? editing
                  ? 'Updating...'
                  : 'Submitting...'
                : editing
                  ? 'Update Plan'
                  : 'Submit Plan'}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Paper>
  );
}

export default DailyWorkPlanCard;
